import { API_URL } from '@/config';
import { formatDateTime } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Vote, Trophy, Users, Calendar, Filter, Search, Copy, Check, Eye, Mail, Phone, Send, MessageSquare, BarChart3, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';

const API = `${API_URL}/api`;

// Format category for display - truncate long names
const formatCategoryDisplay = (category) => {
  if (!category) return '-';
  // If it matches the pattern "X. name...", just return as is (already formatted by backend)
  if (/^\d+\.\s/.test(category)) {
    // Truncate if too long
    return category.length > 45 ? category.substring(0, 42) + '...' : category;
  }
  // Otherwise format it
  return category.length > 45 ? category.substring(0, 42) + '...' : category;
};

const AdminVotesPage = () => {
  const { user } = useOutletContext();
  const [votingEvents, setVotingEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventVotes, setEventVotes] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(null);
  
  // Voters Dialog
  const [votersDialogOpen, setVotersDialogOpen] = useState(false);
  const [votersData, setVotersData] = useState(null);
  const [loadingVoters, setLoadingVoters] = useState(false);
  
  // Category Stats Dialog
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryStats, setCategoryStats] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  // Notification Dialog
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [notifyForm, setNotifyForm] = useState({
    recipient_type: 'nominees',
    message_type: 'email',
    subject: '',
    message: '',
    category: ''
  });
  const [sendingNotification, setSendingNotification] = useState(false);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    loadVotingEvents();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      loadEventVotes(selectedEvent);
    }
  }, [selectedEvent, categoryFilter]);

  const loadVotingEvents = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/votes/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVotingEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEvent(response.data[0].event_id);
      }
    } catch (error) {
      toast.error('Failed to load voting events');
    } finally {
      setLoading(false);
    }
  };

  const loadEventVotes = async (eventId) => {
    try {
      const token = localStorage.getItem('session_token');
      const url = categoryFilter === 'all' 
        ? `${API}/votes/admin/event/${eventId}`
        : `${API}/votes/admin/event/${eventId}?category=${encodeURIComponent(categoryFilter)}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEventVotes(response.data);
    } catch (error) {
      toast.error('Failed to load vote details');
    }
  };

  const loadVoters = async () => {
    if (!selectedEvent) return;
    setLoadingVoters(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/votes/admin/event/${selectedEvent}/voters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVotersData(response.data);
      setVotersDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load voters data');
    } finally {
      setLoadingVoters(false);
    }
  };

  const loadCategoryStats = async () => {
    if (!selectedEvent) return;
    setLoadingCategories(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/votes/admin/event/${selectedEvent}/category-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategoryStats(response.data);
      setCategoryDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load category statistics');
    } finally {
      setLoadingCategories(false);
    }
  };

  const sendNotification = async () => {
    if (!notifyForm.message) {
      toast.error('Please enter a message');
      return;
    }
    if (notifyForm.message_type === 'email' && !notifyForm.subject) {
      toast.error('Please enter an email subject');
      return;
    }

    setSendingNotification(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/votes/admin/event/${selectedEvent}/notify`,
        notifyForm,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      toast.success(`${response.data.sent_count} notifications sent successfully!`);
      setNotifyDialogOpen(false);
      setNotifyForm({ recipient_type: 'nominees', message_type: 'email', subject: '', message: '', category: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notifications');
    } finally {
      setSendingNotification(false);
    }
  };

  const exportVoters = async (format) => {
    if (!selectedEvent) return;
    
    setExporting(format);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(
        `${API}/votes/admin/event/${selectedEvent}/voters/export?format=${format}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      link.setAttribute('download', `voters_${selectedEvent}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Voters exported to ${format.toUpperCase()} successfully!`);
    } catch (error) {
      toast.error(`Failed to export voters: ${error.response?.data?.detail || error.message}`);
    } finally {
      setExporting(null);
    }
  };

  const getVotingLink = (submissionId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/vote/${submissionId}`;
  };

  const copyVotingLink = async (submissionId) => {
    const link = getVotingLink(submissionId);
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(submissionId);
      toast.success('Voting link copied!');
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const getMaxVotes = () => {
    if (!eventVotes?.nominees?.length) return 1;
    return Math.max(...eventVotes.nominees.map(n => n.vote_count), 1);
  };

  const filteredNominees = eventVotes?.nominees?.filter(nominee => {
    if (!searchTerm) return true;
    return nominee.nominee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           nominee.nominee_email?.toLowerCase().includes(searchTerm.toLowerCase());
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#D4AF37' }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-votes-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Votes & Polling</h1>
          <p className="text-sm text-muted-foreground">Manage event voting, view results, and send notifications</p>
        </div>
        {selectedEvent && (
          <Button 
            onClick={() => setNotifyDialogOpen(true)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            data-testid="send-notification-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Send Notification
          </Button>
        )}
      </div>

      {votingEvents.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <Vote className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">No Voting Events</h3>
            <p className="text-muted-foreground">
              Enable "Polling/Voting Required" on an event to start collecting votes.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Event Selector and Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={selectedEvent || ''} onValueChange={setSelectedEvent}>
              <SelectTrigger className="w-full sm:w-[300px]" data-testid="event-selector">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Select an event" />
              </SelectTrigger>
              <SelectContent>
                {votingEvents.map((event) => (
                  <SelectItem key={event.event_id} value={event.event_id}>
                    {event.title} ({event.total_votes} votes)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {eventVotes?.categories?.length > 0 && (
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-[200px]" data-testid="category-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories ({eventVotes.total_nominees})</SelectItem>
                  {eventVotes.categories.map((cat) => {
                    const count = eventVotes.nominees?.filter(n => n.category === cat).length || 0;
                    return (
                      <SelectItem key={cat} value={cat}>{cat} ({count})</SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}

            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search nominees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          {eventVotes && (
            <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
              <Card>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-3 rounded-full bg-blue-100">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{eventVotes.total_nominees}</p>
                    <p className="text-sm text-muted-foreground">Nominees</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-3 rounded-full bg-purple-100">
                    <Vote className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{eventVotes.total_votes}</p>
                    <p className="text-sm text-muted-foreground">Total Votes</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={loadCategoryStats}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-3 rounded-full" style={{ backgroundColor: '#D4AF3720' }}>
                    <BarChart3 className="w-6 h-6" style={{ color: '#D4AF37' }} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{eventVotes.categories?.length || 0}</p>
                    <p className="text-sm text-muted-foreground">Categories</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={loadVoters}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-3 rounded-full bg-green-100">
                    <Eye className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-green-600">View Voters</p>
                    <p className="text-sm text-muted-foreground">Email & Phone</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setNotifyDialogOpen(true)}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-3 rounded-full bg-indigo-100">
                    <Send className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-indigo-600">Notify</p>
                    <p className="text-sm text-muted-foreground">Send Message</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Category Breakdown Section */}
          {eventVotes?.category_breakdown?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BarChart3 className="w-5 h-5" style={{ color: '#D4AF37' }} />
                  Category-wise Vote Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {eventVotes.category_breakdown.map((cat, index) => (
                    <div 
                      key={cat.category} 
                      className={`p-3 rounded-lg border ${categoryFilter === cat.category ? 'border-2 border-amber-500 bg-amber-50' : 'bg-gray-50'} cursor-pointer hover:shadow-md transition-all`}
                      onClick={() => setCategoryFilter(categoryFilter === cat.category ? 'all' : cat.category)}
                      title={cat.category || 'Uncategorized'}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-xs truncate" title={cat.category}>
                            {formatCategoryDisplay(cat.category) || 'Uncategorized'}
                          </h4>
                          <p className="text-xs text-muted-foreground">{cat.nominees} nominees</p>
                        </div>
                        <Badge 
                          className={`ml-2 shrink-0 ${index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-gray-200 text-gray-700'}`}
                        >
                          #{index + 1}
                        </Badge>
                      </div>
                      <div className="flex items-end gap-2">
                        <span className="text-xl font-bold" style={{ color: '#D4AF37' }}>{cat.votes}</span>
                        <span className="text-xs text-muted-foreground mb-0.5">votes</span>
                      </div>
                      <Progress 
                        value={eventVotes.total_votes > 0 ? (cat.votes / eventVotes.total_votes) * 100 : 0} 
                        className="h-1.5 mt-2"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {eventVotes.total_votes > 0 ? Math.round((cat.votes / eventVotes.total_votes) * 100) : 0}% of total
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Nominees Table */}
          {eventVotes && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5" style={{ color: '#D4AF37' }} />
                  Nominees & Vote Count
                  {categoryFilter !== 'all' && (
                    <Badge variant="outline" className="ml-2 max-w-[200px] truncate">{formatCategoryDisplay(categoryFilter)}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {filteredNominees.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No nominees found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Nominee</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Votes</TableHead>
                          <TableHead>Progress</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredNominees.map((nominee, index) => (
                          <TableRow key={nominee.submission_id} data-testid={`nominee-row-${nominee.submission_id}`}>
                            <TableCell>
                              {index < 3 ? (
                                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-white font-bold ${
                                  index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-amber-600'
                                }`}>
                                  {index + 1}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">{index + 1}</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                {nominee.nominee_photo ? (
                                  <img 
                                    src={getImageUrl(nominee.nominee_photo)} 
                                    alt={nominee.nominee_name}
                                    className="w-10 h-10 rounded-full object-cover"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                                    <span className="text-lg font-medium text-gray-500">
                                      {nominee.nominee_name?.charAt(0)?.toUpperCase()}
                                    </span>
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium">{nominee.nominee_name}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {nominee.nominee_email && (
                                  <a href={`mailto:${nominee.nominee_email}`} className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                                    <Mail className="w-3 h-3" />
                                    {nominee.nominee_email}
                                  </a>
                                )}
                                {nominee.nominee_phone && (
                                  <a href={`tel:${nominee.nominee_phone}`} className="flex items-center gap-1 text-xs text-green-600 hover:underline">
                                    <Phone className="w-3 h-3" />
                                    {nominee.nominee_phone}
                                  </a>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {nominee.category ? (
                                <Badge variant="outline" className="text-xs max-w-[180px] truncate" title={nominee.category}>
                                  {formatCategoryDisplay(nominee.category)}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="text-xl font-bold" style={{ color: '#D4AF37' }}>
                                {nominee.vote_count}
                              </span>
                            </TableCell>
                            <TableCell className="w-32">
                              <Progress 
                                value={(nominee.vote_count / getMaxVotes()) * 100} 
                                className="h-2"
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => copyVotingLink(nominee.submission_id)}
                                className="flex items-center gap-1"
                              >
                                {copiedLink === nominee.submission_id ? (
                                  <>
                                    <Check className="w-3 h-3 text-green-500" />
                                    Copied
                                  </>
                                ) : (
                                  <>
                                    <Copy className="w-3 h-3" />
                                    Copy Link
                                  </>
                                )}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Voters Dialog */}
      <Dialog open={votersDialogOpen} onOpenChange={setVotersDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Voter Details - {votingEvents.find(e => e.event_id === selectedEvent)?.title}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => exportVoters('excel')}
                  disabled={exporting === 'excel' || !votersData?.voters?.length}
                  className="text-green-600 border-green-300 hover:bg-green-50"
                  data-testid="export-voters-excel"
                >
                  {exporting === 'excel' ? (
                    <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <FileSpreadsheet className="w-4 h-4 mr-1" />
                      Export All ({votersData?.total_votes || 0})
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => exportVoters('pdf')}
                  disabled={exporting === 'pdf' || !votersData?.voters?.length}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  data-testid="export-voters-pdf"
                >
                  {exporting === 'pdf' ? (
                    <div className="animate-spin h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-1" />
                      PDF
                    </>
                  )}
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {loadingVoters ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#D4AF37' }}></div>
            </div>
          ) : votersData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-blue-50 text-center">
                  <p className="text-3xl font-bold text-blue-600">{votersData.total_voters}</p>
                  <p className="text-sm text-blue-700">Unique Voters (shown)</p>
                </div>
                <div className="p-4 rounded-lg bg-purple-50 text-center">
                  <p className="text-3xl font-bold text-purple-600">{votersData.total_votes}</p>
                  <p className="text-sm text-purple-700">Total Votes Cast</p>
                </div>
              </div>
              
              {votersData.has_more && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-center">
                  <p className="text-sm text-amber-800">
                    Showing first {votersData.voters?.length} unique voters. Use <strong>Export All</strong> to download complete data with all {votersData.total_votes} votes.
                  </p>
                </div>
              )}

              {votersData.voters?.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>#</TableHead>
                        <TableHead>Voter Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Votes</TableHead>
                        <TableHead>First Vote</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {votersData.voters.map((voter, index) => (
                        <TableRow key={voter.voter_email} data-testid={`voter-row-${index}`}>
                          <TableCell className="font-medium">{index + 1}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-medium text-sm">
                                {voter.voter_name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              {voter.voter_name || '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            {voter.voter_email ? (
                              <a href={`mailto:${voter.voter_email}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                                <Mail className="w-3 h-3" />
                                {voter.voter_email}
                              </a>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            {voter.voter_phone ? (
                              <a href={`tel:${voter.voter_phone}`} className="flex items-center gap-1 text-green-600 hover:underline">
                                <Phone className="w-3 h-3" />
                                {voter.voter_phone}
                              </a>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{voter.votes_cast}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {voter.first_vote_at ? formatDateTime(voter.first_vote_at) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Vote className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No votes yet for this event</p>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Category Stats Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" style={{ color: '#D4AF37' }} />
              Category Statistics - {votingEvents.find(e => e.event_id === selectedEvent)?.title}
            </DialogTitle>
          </DialogHeader>
          
          {loadingCategories ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#D4AF37' }}></div>
            </div>
          ) : categoryStats ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-blue-50 text-center">
                  <p className="text-3xl font-bold text-blue-600">{categoryStats.total_categories}</p>
                  <p className="text-sm text-blue-700">Categories</p>
                </div>
                <div className="p-4 rounded-lg bg-green-50 text-center">
                  <p className="text-3xl font-bold text-green-600">{categoryStats.total_nominees}</p>
                  <p className="text-sm text-green-700">Total Nominees</p>
                </div>
                <div className="p-4 rounded-lg bg-purple-50 text-center">
                  <p className="text-3xl font-bold text-purple-600">{categoryStats.total_votes}</p>
                  <p className="text-sm text-purple-700">Total Votes</p>
                </div>
              </div>

              <Tabs defaultValue={categoryStats.categories?.[0]?.category || ''} className="w-full">
                <TabsList className="flex flex-wrap h-auto gap-2">
                  {categoryStats.categories?.map((cat) => (
                    <TabsTrigger key={cat.category} value={cat.category} className="flex items-center gap-2">
                      {cat.category}
                      <Badge variant="secondary" className="ml-1">{cat.nominee_count}</Badge>
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {categoryStats.categories?.map((cat) => (
                  <TabsContent key={cat.category} value={cat.category} className="mt-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-center">
                          <CardTitle className="text-lg">{cat.category}</CardTitle>
                          <div className="flex gap-4 text-sm">
                            <span><strong>{cat.nominee_count}</strong> nominees</span>
                            <span><strong>{cat.total_votes}</strong> votes</span>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>#</TableHead>
                              <TableHead>Nominee</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Phone</TableHead>
                              <TableHead>Votes</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {cat.nominees.map((nominee, idx) => (
                              <TableRow key={nominee.submission_id}>
                                <TableCell>
                                  {idx < 3 ? (
                                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold ${
                                      idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : 'bg-amber-600'
                                    }`}>
                                      {idx + 1}
                                    </span>
                                  ) : (
                                    idx + 1
                                  )}
                                </TableCell>
                                <TableCell className="font-medium">{nominee.nominee_name}</TableCell>
                                <TableCell>
                                  {nominee.nominee_email ? (
                                    <a href={`mailto:${nominee.nominee_email}`} className="text-blue-600 hover:underline text-sm">
                                      {nominee.nominee_email}
                                    </a>
                                  ) : '-'}
                                </TableCell>
                                <TableCell>
                                  {nominee.nominee_phone ? (
                                    <a href={`tel:${nominee.nominee_phone}`} className="text-green-600 hover:underline text-sm">
                                      {nominee.nominee_phone}
                                    </a>
                                  ) : '-'}
                                </TableCell>
                                <TableCell>
                                  <span className="font-bold" style={{ color: '#D4AF37' }}>{nominee.vote_count}</span>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Send Notification Dialog */}
      <Dialog open={notifyDialogOpen} onOpenChange={setNotifyDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Send Notification - {votingEvents.find(e => e.event_id === selectedEvent)?.title}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Send To</Label>
                <Select 
                  value={notifyForm.recipient_type} 
                  onValueChange={(v) => setNotifyForm({...notifyForm, recipient_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nominees">All Nominees</SelectItem>
                    <SelectItem value="voters">All Voters</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Send Via</Label>
                <Select 
                  value={notifyForm.message_type} 
                  onValueChange={(v) => setNotifyForm({...notifyForm, message_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">
                      <span className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email</span>
                    </SelectItem>
                    <SelectItem value="sms">
                      <span className="flex items-center gap-2"><MessageSquare className="w-4 h-4" /> SMS</span>
                    </SelectItem>
                    <SelectItem value="whatsapp">
                      <span className="flex items-center gap-2"><Phone className="w-4 h-4" /> WhatsApp</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {notifyForm.recipient_type === 'nominees' && eventVotes?.categories?.length > 0 && (
              <div className="space-y-2">
                <Label>Filter by Category (Optional)</Label>
                <Select 
                  value={notifyForm.category || 'all'} 
                  onValueChange={(v) => setNotifyForm({...notifyForm, category: v === 'all' ? '' : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {eventVotes.categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {notifyForm.message_type === 'email' && (
              <div className="space-y-2">
                <Label>Subject *</Label>
                <Input
                  value={notifyForm.subject}
                  onChange={(e) => setNotifyForm({...notifyForm, subject: e.target.value})}
                  placeholder="Enter email subject"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                value={notifyForm.message}
                onChange={(e) => setNotifyForm({...notifyForm, message: e.target.value})}
                placeholder="Enter your message..."
                rows={5}
              />
            </div>

            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Emails sent from <strong>jaipurlionsclub@gmail.com</strong>
              </p>
              {notifyForm.message_type === 'sms' && (
                <p className="text-sm text-amber-700 mt-1">
                  SMS requires Twilio paid account with international permissions for India numbers.
                </p>
              )}
              {notifyForm.message_type === 'whatsapp' && (
                <p className="text-sm text-amber-700 mt-1">
                  WhatsApp: Recipients must first message your Twilio sandbox number to opt-in.
                  Send "join &lt;sandbox-code&gt;" to +1 415 523 8886 on WhatsApp.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setNotifyDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={sendNotification} 
                disabled={sendingNotification}
                className="bg-gradient-to-r from-blue-600 to-purple-600"
              >
                {sendingNotification ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Sending...
                  </span>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Notification
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVotesPage;
